import { useEffect, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Heart, ArrowRight, MapPin } from "lucide-react";
import { PremiumBadge, LevelBadge } from "@/components/Badges";
import { toast } from "sonner";
import { labelTalentType } from "@/lib/roleCategories";

export default function ProFavorites() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    if (!loading && user && (user.role !== "creator" && user.role !== "professional")) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if ((user?.role === "creator" || user?.role === "professional")) load();
  }, [user]);

  const load = async () => {
    try {
      const { data } = await api.get("/favorites");
      setFavs(Array.isArray(data) ? data : []);
    } catch {
      setFavs([]);
    }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/favorites/${uid}`);
      toast.success("Retiré des favoris");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <AppShell>
      <div data-testid="pro-favorites-page">
        <div className="mb-8">
          <p className="caption-label text-[#C68E59]">Favoris</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Vos talents préférés</h1>
          <p className="text-[#666] mt-2 text-sm">Gardez une liste des talents qui vous inspirent.</p>
        </div>

        {favs.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#EADDC9] p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#FDF2F2] flex items-center justify-center mx-auto mb-4">
              <Heart className="w-6 h-6 text-[#D32F2F]" />
            </div>
            <p className="font-serif text-xl text-[#1A1A1A] mb-2">Aucun favori pour l'instant</p>
            <p className="text-sm text-[#8B8680] mb-6">Explorez la galerie de talents et cliquez sur ♡ pour sauvegarder.</p>
            <Link to="/createurs/talents">
              <button className="inline-flex items-center gap-2 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-xs font-semibold px-5 py-2.5 transition">
                Explorer les talents <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {favs.map((t) => (
            <div key={t.user_id} className="group relative bg-white rounded-2xl border border-[#EADDC9] overflow-hidden flex flex-col" data-testid={`fav-${t.user_id}`}>
              <Link to={`/talents/${t.user_id}`} className="flex flex-col flex-1">
                <div className="aspect-[4/5] bg-[#FAF3EA] relative shrink-0">
                  <FavImage userId={t.user_id} demo={t.demo_photo_url} />
                  {t.is_premium && <div className="absolute top-2 right-2"><PremiumBadge /></div>}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="font-serif text-lg text-[#1A1A1A] leading-tight">{t.name}</p>
                  <p className="text-xs text-[#8B8680]">{labelTalentType(t.talent_type) || "Talent"}</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-[#666]">
                    <MapPin className="w-3 h-3" />{t.location || "—"}
                  </div>
                  <div className="mt-auto pt-2"><LevelBadge level={t.level} /></div>
                </div>
              </Link>
              <button
                onClick={() => remove(t.user_id)}
                className="absolute top-2 left-2 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white transition"
                data-testid={`unfav-${t.user_id}`}
                title="Retirer des favoris"
              >
                <Heart className="w-4 h-4 text-[#D32F2F] fill-[#D32F2F]" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function FavImage({ userId, demo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    api.get(`/portfolio/${userId}`).then(({ data }) => {
      const main = data.find((p) => p.is_main) || data[0];
      if (main) setUrl(optimizeUrl(`${API}/files/${main.storage_path}`, 640));
      else if (demo) setUrl(demo);
    });
  }, [userId, demo]);
  if (!url) return <div className="w-full h-full bg-[#FAF3EA]" />;
  return <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />;
}
